if (!customElements.get('collection-product-focus')) {
  class CollectionProductFocus extends HTMLElement {
    connectedCallback() {
      this.responses = new Map();
      this.appliedCards = new WeakSet();
      this.onHistoryChange = () => this.scheduleUpdate();
      this.observer = new MutationObserver(() => this.scheduleUpdate());
      this.observer.observe(this, { childList: true, subtree: true });
      window.addEventListener('popstate', this.onHistoryChange);
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this.observer.disconnect();
      window.removeEventListener('popstate', this.onHistoryChange);
    }

    getSelection() {
      const params = new URLSearchParams(window.location.search);
      return {
        handle: (params.get('handle') || '').trim().toLowerCase(),
        color: (params.get('color') || '').trim(),
        hasFilters: Array.from(params.keys()).some((key) => key.startsWith('filter.'))
      };
    }

    colorKey(value) {
      return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .replace(/['"]/g, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');
    }

    findSwatch(column, color) {
      const name = color.toLowerCase();
      const key = this.colorKey(color);
      return Array.from(column.querySelectorAll('.product-card-swatch')).find((swatch) =>
        (swatch.dataset.color || '').toLowerCase() === name || swatch.dataset.colorKey === key
      );
    }

    scheduleUpdate() {
      if (this.updating) {
        this.updateAgain = true;
        return;
      }
      if (this.scheduled) return;
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        if (this.isConnected) this.update();
      });
    }

    preservePageLinks(selection) {
      this.querySelectorAll('pagination-theme a[href]').forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set('handle', selection.handle);
        if (selection.color) url.searchParams.set('color', selection.color);
        link.href = url.href;
      });
    }

    pinProduct(grid, column) {
      if (column.parentElement !== grid) {
        grid.insertBefore(column, grid.querySelector(':scope > .loading-overlay'));
      }
      const products = Array.from(grid.children).filter((item) => item.hasAttribute('data-collection-product-handle'));
      const index = products.indexOf(column);
      if (index <= 0) return;
      const positions = products.slice(0, index + 1).map((item) => {
        const position = document.createComment('');
        item.replaceWith(position);
        return position;
      });
      const reordered = [column, ...products.slice(0, index)];
      positions.forEach((position, i) => position.replaceWith(reordered[i]));
    }

    async loadCard(selection) {
      let variant;
      if (selection.color) {
        const root = window.Shopify?.routes?.root || '/';
        const productURL = new URL(`${root}products/${encodeURIComponent(selection.handle)}.js`, window.location.origin);
        const response = await fetch(productURL);
        if (!response.ok) return null;
        const product = await response.json();
        const colorIndex = product.options.findIndex((option) =>
          /color|colour|couleur|farbe/i.test(typeof option === 'string' ? option : option.name)
        );
        if (colorIndex !== -1) {
          const variants = product.variants.filter((item) =>
            this.colorKey(item.options[colorIndex]) === this.colorKey(selection.color)
          );
          variant = variants.find((item) => item.available) || variants[0];
        }
      }

      const collectionURL = new URL(this.dataset.collectionUrl, window.location.origin);
      const url = new URL(`${collectionURL.pathname.replace(/\/$/, '')}/products/${encodeURIComponent(selection.handle)}`, window.location.origin);
      url.searchParams.set('section_id', 'main-collection-product-grid');
      if (variant) url.searchParams.set('variant', variant.id);
      const previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      const response = await fetch(url);
      return response.ok ? response.text() : null;
    }

    async update() {
      this.updating = true;
      try {
        const selection = this.getSelection();
        if (!selection.handle || /[/?#]/.test(selection.handle)) return;
        const key = JSON.stringify([selection.handle, selection.color]);
        if (this.selectionKey !== key) {
          this.selectionKey = key;
          this.appliedCards = new WeakSet();
        }
        this.preservePageLinks(selection);
        await customElements.whenDefined('product-card');
        if (!this.isConnected) return;
        const grid = this.querySelector('#product-grid');
        if (!grid) return;
        const matchingColumns = () => Array.from(grid.children).filter((column) =>
          column.dataset.collectionProductHandle === selection.handle
        );
        let column = matchingColumns()[0];
        if (!column && selection.hasFilters) return;
        if (!column || (selection.color && !this.appliedCards.has(column) && !this.findSwatch(column, selection.color))) {
          if (!this.responses.has(key)) {
            this.responses.set(key, this.loadCard(selection).catch((error) => {
              console.warn('Unable to load the collection product from the URL.', error);
              return null;
            }));
          }
          const html = await this.responses.get(key);
          const current = this.getSelection();
          if (!this.isConnected || grid !== this.querySelector('#product-grid') ||
              selection.handle !== current.handle || selection.color !== current.color) return;
          if (html) {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const replacement = parsed.querySelector('[data-collection-product-handle]');
            if (replacement?.dataset.collectionProductHandle === selection.handle) {
              matchingColumns()[0]?.replaceWith(replacement);
              column = replacement;
            }
          }
        }
        if (!column) return;
        matchingColumns().forEach((item) => {
          if (item !== column) item.remove();
        });
        this.pinProduct(grid, column);
        if (!this.appliedCards.has(column)) {
          if (selection.color) this.findSwatch(column, selection.color)?.click();
          this.appliedCards.add(column);
        }
      } catch (error) {
        console.warn('Unable to load the collection product from the URL.', error);
      } finally {
        this.updating = false;
        if (this.updateAgain) {
          this.updateAgain = false;
          this.scheduleUpdate();
        }
      }
    }
  }

  customElements.define('collection-product-focus', CollectionProductFocus);
}
